/**
 * useMapLabelPositions Hook
 * 将 Studio 的 labelPosition (像素偏移) 转换为容器内像素坐标
 *
 * labelPosition: [offsetX, offsetY] — 相对于蓝点(badge)位置的像素偏移
 * 偏移值基于参考分辨率 1920×1080 调整
 * 在不同分辨率下，偏移量会按 chartWidth/REFERENCE_WIDTH 等比缩放
 * 确保 1K / 1080p / 2K / 4K / MacBook Pro 等分辨率下视觉一致
 *
 * 蓝点位置由 ECharts convertToPixel 计算，在任何分辨率下都准确
 * 标签位置 = 蓝点位置 + 缩放后的偏移
 *
 * 监听 resize 事件实时更新
 *
 * 锚点规则：始终取标签的左侧或右侧边缘中点（取决于蓝点在哪一侧）
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { EChartsType } from 'echarts';
import {
  IStudio,
  COUNTRY_COORDINATES,
  NEW_COUNTRY_COORDINATES,
} from '../../worldMap/const/studio';

export interface ILabelPosition {
  studioName: string;
  /** 标签左上角 x (px) */
  x: number;
  /** 标签左上角 y (px) */
  y: number;
  /** 引导线锚点 x — 标签左/右边缘中点 */
  anchorX: number;
  /** 引导线锚点 y */
  anchorY: number;
  /** 该 studio 对应的地理坐标 (用于蓝点位置) */
  coord: [number, number];
}

interface IUseMapLabelPositionsParams {
  chartInstance: EChartsType | null;
  studios: IStudio[];
  containerRef: React.RefObject<HTMLDivElement>;
  chartRef: React.RefObject<HTMLDivElement>;
}

/** 估算标签宽度 (px) */
const LABEL_EST_WIDTH = 170;
/** Row1 高度 (icon + name + stars row) — 水平线锚点在 row1 下方 */
const ROW1_HEIGHT = 26;

/** labelPosition 偏移值的参考分辨率 (宽×高) — studio.ts 中的偏移基于此尺寸调试 */
const REFERENCE_WIDTH = 1622;
const REFERENCE_HEIGHT = 804;

const useMapLabelPositions = ({
  chartInstance,
  studios,
  containerRef,
  chartRef,
}: IUseMapLabelPositionsParams): ILabelPosition[] => {
  const [positions, setPositions] = useState<ILabelPosition[]>([]);
  const rafRef = useRef<number>(0);

  const calculatePositions = useCallback(() => {
    if (!chartInstance || !containerRef.current || !chartRef.current) {
      return;
    }

    const containerRect = containerRef.current.getBoundingClientRect();
    const chartRect = chartRef.current.getBoundingClientRect();
    const chartOffsetX = chartRect.left - containerRect.left;
    const chartOffsetY = chartRect.top - containerRect.top;
    // 按当前图表容器尺寸与参考尺寸的比例缩放偏移量
    const scaleX = chartRect.width / REFERENCE_WIDTH;
    const scaleY = chartRect.height / REFERENCE_HEIGHT;

    const newPositions: ILabelPosition[] = [];

    const processStudio = (
      studioName: string,
      country: string,
      labelPos?: [number, number],
    ) => {
      if (!labelPos) return;

      const coordinates =
        NEW_COUNTRY_COORDINATES[country] || COUNTRY_COORDINATES[country];
      if (!coordinates || coordinates.length === 0) return;
      const coord = coordinates[0];

      // 蓝点像素位置 (地理坐标 → px，任何分辨率下都准确)
      const pixel = chartInstance.convertToPixel({ geoIndex: 0 }, coord);
      if (!pixel || pixel.length < 2) return;
      const badgeX = chartOffsetX + pixel[0];
      const badgeY = chartOffsetY + pixel[1];

      // 标签像素位置 = 蓝点位置 + 按分辨率缩放的偏移
      const labelX = badgeX + labelPos[0] * scaleX;
      const labelY = badgeY + labelPos[1] * scaleY;

      // 锚点始终在标签的左或右边缘，y 在 row1 底部（name 行下方）
      const labelCenterX = labelX + LABEL_EST_WIDTH / 2;
      const labelAnchorY = labelY + ROW1_HEIGHT;

      let anchorX: number;
      if (badgeX < labelCenterX) {
        anchorX = labelX;
      } else {
        anchorX = labelX + LABEL_EST_WIDTH;
      }

      newPositions.push({
        studioName,
        x: labelX,
        y: labelY,
        anchorX,
        anchorY: labelAnchorY,
        coord,
      });
    };

    studios.forEach(studio => {
      processStudio(studio.name, studio.country, studio.labelPosition);
    });

    setPositions(newPositions);
  }, [chartInstance, studios, containerRef, chartRef]);

  const scheduleUpdate = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }
    rafRef.current = requestAnimationFrame(() => {
      calculatePositions();
    });
  }, [calculatePositions]);

  useEffect(() => {
    if (!chartInstance) return;

    calculatePositions();
    chartInstance.on('georoam', scheduleUpdate);
    chartInstance.on('finished', scheduleUpdate);
    window.addEventListener('resize', scheduleUpdate);

    return () => {
      chartInstance.off('georoam', scheduleUpdate);
      chartInstance.off('finished', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [chartInstance, calculatePositions, scheduleUpdate]);

  return positions;
};

export default useMapLabelPositions;
